import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  ExternalLink,
  Filter,
  Trash2,
  ChevronDown
} from 'lucide-react';
import {
  getNotifications,
  markNotificationRead,
  markNotificationUnread,
  markAllNotificationsRead
} from '../services/api';
import './Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();

  const notificationTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'announcement', label: 'Announcements' },
    { value: 'alert', label: 'Alerts' },
    { value: 'update', label: 'Updates' },
    { value: 'info', label: 'Info' },
    { value: 'warning', label: 'Warnings' },
    { value: 'success', label: 'Success' }
  ];

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [notifications, filter, typeFilter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await getNotifications();
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...notifications];

    // Filter by read status
    if (filter === 'unread') {
      filtered = filtered.filter(n => !n.isRead);
    } else if (filter === 'read') {
      filtered = filtered.filter(n => n.isRead);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(n => n.type === typeFilter);
    }

    setFilteredNotifications(filtered);
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await markNotificationRead(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAsUnread = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await markNotificationUnread(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsRead();
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if not already
      if (!notification.isRead) {
        await markNotificationRead(notification._id);
        fetchNotifications();
      }

      // Navigate to action URL if exists
      if (notification.actionUrl) {
        if (notification.actionUrl.startsWith('http')) {
          window.open(notification.actionUrl, '_blank');
        } else {
          navigate(notification.actionUrl);
        }
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      announcement: '#2196F3',
      alert: '#f44336',
      update: '#4CAF50',
      info: '#00bcd4',
      warning: '#ff9800',
      success: '#8bc34a'
    };
    return colors[type] || '#2196F3';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <div className="notifications-header-content">
          <h1>
            <Bell size={32} />
            Notifications
          </h1>
          <p className="notifications-count">
            {unreadCount > 0 ? (
              <span className="unread-count">{unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}</span>
            ) : (
              <span>All caught up!</span>
            )}
          </p>
        </div>

        <div className="notifications-header-actions">
          {unreadCount > 0 && (
            <button className="btn-mark-all-read" onClick={handleMarkAllAsRead}>
              <Check size={18} />
              Mark all as read
            </button>
          )}
          <button
            className="btn-filter"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={18} />
            Filters
            <ChevronDown size={16} className={showFilters ? 'rotated' : ''} />
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="notifications-filters">
          <div className="filter-group">
            <label>Status</label>
            <div className="filter-buttons">
              <button
                className={filter === 'all' ? 'active' : ''}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={filter === 'unread' ? 'active' : ''}
                onClick={() => setFilter('unread')}
              >
                Unread
              </button>
              <button
                className={filter === 'read' ? 'active' : ''}
                onClick={() => setFilter('read')}
              >
                Read
              </button>
            </div>
          </div>

          <div className="filter-group">
            <label>Type</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              {notificationTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="notifications-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <Bell size={64} />
            <h3>No notifications found</h3>
            <p>
              {filter === 'unread'
                ? "You're all caught up! No unread notifications."
                : filter === 'read'
                ? "No read notifications yet."
                : "You don't have any notifications yet."}
            </p>
          </div>
        ) : (
          <div className="notifications-list-page">
            {filteredNotifications.map((notification) => (
              <div
                key={notification._id}
                className={`notification-card-page ${!notification.isRead ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div
                  className="notification-card-icon"
                  style={{ background: `${getTypeColor(notification.type)}20` }}
                >
                  <span style={{ fontSize: '32px' }}>{notification.icon}</span>
                </div>

                <div className="notification-card-content">
                  <div className="notification-card-header-page">
                    <div className="notification-title-row">
                      <h3>{notification.title}</h3>
                      {!notification.isRead && <span className="unread-indicator">New</span>}
                    </div>
                    <span
                      className="notification-type-badge"
                      style={{ background: getTypeColor(notification.type), color: 'white' }}
                    >
                      {notification.type}
                    </span>
                  </div>

                  <p className="notification-card-message">{notification.message}</p>

                  <div className="notification-card-footer-page">
                    <span className="notification-card-time">{formatDate(notification.createdAt)}</span>
                    <div className="notification-card-actions">
                      {notification.actionUrl && (
                        <span className="action-link-indicator">
                          <ExternalLink size={14} />
                          Click to view
                        </span>
                      )}
                      {notification.isRead ? (
                        <button
                          className="btn-mark-unread"
                          onClick={(e) => handleMarkAsUnread(notification._id, e)}
                        >
                          Mark as unread
                        </button>
                      ) : (
                        <button
                          className="btn-mark-read"
                          onClick={(e) => handleMarkAsRead(notification._id, e)}
                        >
                          <Check size={14} />
                          Mark as read
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
